import { ark } from "@ark-ui/react/factory"
import { Loader } from "@ui/Loader/Loader"
import type { ComponentProps, ComponentType, FC, SVGProps } from "react"
import { styled } from "styled-system/jsx"

type ButtonExtraProps = {
  loading?: boolean | undefined
  icon?: ComponentType<SVGProps<SVGSVGElement>> | undefined
}

type BaseButtonProps = ComponentProps<typeof BaseButton>
const BaseButton = styled(ark.button, {
  base: {
    alignItems: "center",
    appearance: "none",
    rounded: true,
    cursor: "pointer",
    display: "inline-flex",
    flexShrink: "0",
    fontWeight: "semibold",
    gap: "xs",
    isolation: "isolate",
    justifyContent: "center",
    outline: "[0]",
    paddingX: "sm",
    paddingY: "sm",
    position: "relative",
    transition: "colors",
    transitionProperty: "[background-color, border-color, color, box-shadow]",
    userSelect: "none",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
    _hover: {
      filter: "[brightness(0.8)]",
    },
    _icon: {
      flexShrink: "0",
      width: "[14px]",
      height: "[14px]",
    },
    _disabled: {
      cursor: "not-allowed",
      opacity: "muted",
    },
    focusVisibleRing: "outside",
  },
  defaultVariants: {
    variant: "secondary",
  },
  variants: {
    variant: {
      primary: {
        background: "primary",
        color: "text.onDark",
      },
      secondary: {
        background: "secondary",
        color: "text",
      },
      ghost: {
        color: "primary",
        _hover: {
          filter: "[none]",
          background: "hover",
        },
      },
      danger: {
        background: "error",
        color: "text.onDark",
      },
      iconButton: {
        color: "primary",
        paddingX: "none",
        paddingY: "none",
        width: "xs",
        height: "xs",
        _hover: {
          filter: "[none]",
          background: "hover",
        },
      },
    },
  },
})

export type ButtonProps = Omit<BaseButtonProps, "colorPalette"> &
  ButtonExtraProps

export const Button: FC<ButtonProps> = (props) => {
  const { loading, icon: Icon, children, ...rest } = props
  return (
    <BaseButton
      type="button"
      {...rest}
      data-loading={loading ? "" : undefined}
      // oxlint-disable-next-line typescript/prefer-nullish-coalescing
      disabled={loading || rest.disabled}
    >
      {!props.asChild && loading ? (
        <Loader>{children}</Loader>
      ) : (
        <>
          {Icon ? <Icon /> : undefined}
          {children}
        </>
      )}
    </BaseButton>
  )
}
